import { useRef } from "react";
import { pixelLead } from "@/lib/pixel";
import { clarityEvent, CLARITY_EVENTS } from "@/lib/clarity";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import QuoteForm from "@/components/corporativo/QuoteForm";
import CallbackForm from "@/components/contact/CallbackForm";
import { PriceAnchor } from "@/components/corporativo/PriceAnchor";
import { LeadMagnet } from "@/components/corporativo/LeadMagnet";
import { FounderSection } from "@/components/FounderSection";
import { Testimonials } from "@/components/Testimonials";
import { ClientLogos } from "@/components/ClientLogos";
import { WorkGallery } from "@/components/WorkGallery";
import { ComoFunciona } from "@/components/corporativo/ComoFunciona";
import { FaqB2B } from "@/components/corporativo/FaqB2B";
import { MIN_UNITS_B2B } from "@/data/pricing";
import {
  Building2,
  NotebookPen,
  Users,
  Palette,
  Truck,
  ShieldCheck,
  MessageCircle,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const WHATSAPP_CORPORATIVO =
  "https://wa.me/50761568575?text=" +
  encodeURIComponent(
    "¡Hola! Quiero cotizar un producto de papelería con mi marca (planner, libreta o agenda). Mi nombre es:"
  );

/**
 * Home — eje B2B del sitio.
 *
 * El comprador corporativo (pediatras, eventos, agencias) compra por necesidad
 * operativa, no por impulso: la primera impresión le habla a él y el CTA
 * primario es cotizar. La tienda B2C queda accesible desde la navegación.
 */
const Index = () => {
  const quoteRef = useRef<HTMLDivElement>(null);

  /** Scroll a cotización: mide intención de compra B2B en Clarity */
  const scrollToQuote = () => {
    clarityEvent(CLARITY_EVENTS.COTIZAR_PEDIDO);
    quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /** El click en WhatsApp de cotización es un Lead B2B: señal clave para Meta Ads */
  const handleCotizarWhatsApp = () => {
    pixelLead();
    clarityEvent(CLARITY_EVENTS.LEAD_WHATSAPP_CORPORATIVO);
    window.open(WHATSAPP_CORPORATIVO, "_blank", "noopener,noreferrer");
  };

  const perfiles = [
    {
      icon: NotebookPen,
      title: "Psicólogos y terapeutas",
      desc: "Tu método en una libreta o planner con tu marca, para que tus pacientes lo usen entre sesión y sesión.",
      ejemplo: "Ideal para: tu libreta o planner con tu nombre",
    },
    {
      icon: Sparkles,
      title: "Coaches y creadores",
      desc: "Tu metodología en papel: un planner que tu comunidad usa cada día y que te recuerda en cada página.",
      ejemplo: "Ideal para: tu planner de metodología propia",
    },
    {
      icon: Users,
      title: "Comunidades y clubes",
      desc: "Diarios y libretas para tu grupo: un objeto físico que lleva tu nombre a la mesa de cada persona.",
      ejemplo: "Ideal para: diarios de lectura o libretas de comunidad",
    },
    {
      icon: Building2,
      title: "Consultorios y empresas",
      desc: "Agendas con tu marca para pacientes, equipos y eventos. El detalle que se queda todo el año.",
      ejemplo: "Ideal para: agenda pediátrica o corporativa con tu logo",
    },
  ];

  const capacidades = [
    {
      icon: Palette,
      title: "Tu marca en cada detalle",
      desc: "Logo, colores corporativos, mensajes internos y portadas diseñadas a medida. La misma personalización artesanal que conoces, aplicada a tu identidad.",
    },
    {
      icon: Truck,
      title: "Producción y entrega coordinada",
      desc: "Cronograma claro desde la aprobación del diseño hasta la entrega en Panamá. Te mantenemos informado en cada etapa.",
    },
    {
      icon: ShieldCheck,
      title: "Calidad revisada pieza por pieza",
      desc: "Control de calidad artesanal en cada unidad antes del empaque. Volumen no significa bajar el estándar.",
    },
    {
      icon: Sparkles,
      title: "Muestra digital antes de producir",
      desc: "Apruebas un preview del diseño con tu marca antes de que salga a producción. Cero sorpresas.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <main>
        {/* ─── Hero B2B ─── */}
        <section className="relative min-h-[55vh] md:min-h-[65vh] flex items-center bg-background overflow-hidden py-16 md:py-20">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?auto=format&fit=crop&w=1600&q=80"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
          </div>
          <div className="container relative z-10 mx-auto px-6 sm:px-8 md:px-12 lg:px-16 flex flex-col items-center justify-center text-center">
            <Badge
              variant="outline"
              className="bg-white/80 backdrop-blur-sm border-primary-dark/30 text-brown text-sm font-medium px-5 py-2 rounded-full mb-6"
            >
              Papelería de marca · Vivi con Amor y Arte
            </Badge>
            <h1 className="font-playfair font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight mb-6 md:mb-8 text-brown-dark max-w-4xl">
              Tu marca, hecha producto{" "}
              <span className="text-primary-dark italic">a tu medida</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl mb-9 md:mb-10 text-brown-light max-w-2xl leading-relaxed">
              Planners, libretas y agendas con tu marca: convertimos tu método o
              tu identidad en un objeto que tu gente usa y guarda. Producción
              artesanal desde {MIN_UNITS_B2B} unidades, con muestra antes de
              producir.
            </p>
            <PriceAnchor />
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in w-full sm:w-auto">
              <Button
                size="lg"
                onClick={scrollToQuote}
                className="bg-primary text-brown-dark hover:bg-primary-dark font-semibold px-10 py-4 rounded-full text-lg shadow-md transition-all duration-300 min-h-[52px]"
              >
                Cotizar mi pedido
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* ─── Perfiles de comprador ─── */}
        <section className="py-20 md:py-24 bg-white">
          <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
            <div className="text-center mb-14 md:mb-16">
              <span className="text-primary-dark font-medium tracking-widest uppercase text-sm">
                {"\u00BF"}Quién compra con nosotros?
              </span>
              <h2 className="font-playfair font-semibold text-3xl sm:text-4xl md:text-5xl mt-3 text-brown">
                Hecho para tu tipo de negocio
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {perfiles.map((p) => (
                <div
                  key={p.title}
                  className="group bg-background rounded-2xl border border-primary/15 p-7 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mb-5 group-hover:bg-primary/35 transition-colors">
                    <p.icon className="h-7 w-7 text-brown" />
                  </div>
                  <h3 className="font-playfair font-semibold text-xl text-brown-dark mb-3">
                    {p.title}
                  </h3>
                  <p className="text-brown-light/90 leading-relaxed text-sm flex-1">
                    {p.desc}
                  </p>
                  <p className="mt-4 pt-4 border-t border-primary/15 text-xs text-primary-dark font-medium">
                    {p.ejemplo}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Producto: tu marca hecha producto ─── */}
        <section className="py-20 md:py-24 bg-gradient-to-b from-background to-peach/30">
          <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
            <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="grid md:grid-cols-2 items-stretch">
                <div className="relative min-h-[280px] md:min-h-[380px]">
                  <img
                    src="https://pub-94b113bd369c41d8b1526589b4cb9b94.r2.dev/images/products/d21a9c26-5505-4b8b-a9c1-93cedac323e8.png"
                    alt="Producto de papelería personalizado con el logo de la marca"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-primary-dark font-medium tracking-widest uppercase text-xs mb-3">
                    Lo que más nos piden
                  </span>
                  <h2 className="font-playfair font-bold text-2xl md:text-4xl text-brown-dark leading-tight mb-4">
                    Así se ve tu marca en papel
                  </h2>
                  <p className="text-brown-light/90 leading-relaxed mb-6">
                    La libreta, el planner o la agenda que tu gente va a usar
                    todo el año. Vos traés tu marca y tu contenido; nosotras el
                    diseño, la muestra y la producción a mano. Es la misma que ya
                    hacen pediatras, psicólogas y coaches con su nombre.
                  </p>
                  <ul className="space-y-2.5 text-sm text-brown-light/90 mb-8">
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="h-4 w-4 mt-0.5 text-primary-dark shrink-0" />
                      Puede ser desde {MIN_UNITS_B2B} unidades, precio especial
                      por volumen
                    </li>
                    <li className="flex items-start gap-2">
                      <Palette className="h-4 w-4 mt-0.5 text-primary-dark shrink-0" />
                      Portada con tu logo, tus colores y los datos de tu marca
                    </li>
                    <li className="flex items-start gap-2">
                      <Truck className="h-4 w-4 mt-0.5 text-primary-dark shrink-0" />
                      Entrega coordinada en tu consultorio, oficina o evento
                    </li>
                  </ul>
                  <div>
                    <Button
                      onClick={scrollToQuote}
                      className="bg-primary text-brown-dark hover:bg-primary-dark font-semibold rounded-full px-8 shadow-md"
                    >
                      Cotizar mi producto
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Capacidades ─── */}
        <section className="py-20 md:py-24 bg-white">
          <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
            <div className="text-center mb-14 md:mb-16">
              <span className="text-primary-dark font-medium tracking-widest uppercase text-sm">
                Cómo trabajamos
              </span>
              <h2 className="font-playfair font-semibold text-3xl sm:text-4xl md:text-5xl mt-3 text-brown">
                Artesanal, incluso a escala
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {capacidades.map((c) => (
                <div
                  key={c.title}
                  className="bg-background rounded-2xl p-7 border border-primary/15"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                      <c.icon className="h-5 w-5 text-brown" />
                    </div>
                    <div>
                      <h3 className="font-playfair font-semibold text-lg text-brown-dark mb-1.5">
                        {c.title}
                      </h3>
                      <p className="text-sm text-brown-light/90 leading-relaxed">
                        {c.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ComoFunciona onCotizar={scrollToQuote} />

        <WorkGallery />
        <Testimonials />
        <ClientLogos />

        {/* Fundadora - sección de confianza */}
        <FounderSection />
        <FaqB2B onCotizar={scrollToQuote} />

        {/* ─── Cotización / CTA final ─── */}
        <section
          ref={quoteRef}
          className="relative py-20 md:py-28 bg-gradient-to-b from-peach/30 via-primary/25 to-primary/40 overflow-hidden"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none select-none absolute -top-10 left-1/2 -translate-x-1/2 font-playfair text-[12rem] md:text-[20rem] leading-none text-white/20"
          >
            "
          </span>
          <div className="container relative z-10 mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-playfair font-bold text-3xl sm:text-4xl md:text-5xl mb-6 text-brown-dark leading-tight">
                Cuéntanos sobre tu pedido
              </h2>
              <p className="text-brown-light text-lg md:text-xl mb-4 leading-relaxed">
                Completá el formulario y Vivi te responde con una propuesta: precios
                por volumen y muestra del diseño antes de producir. Si preferís
                escribirle directo, también podés hacerlo por WhatsApp.
              </p>
              <div className="flex items-center justify-center gap-4 mb-10">
                <img src="/payments/visa.svg" alt="Visa" className="h-6 md:h-7 w-auto" loading="lazy" />
                <img src="/payments/mastercard.svg" alt="Mastercard" className="h-7 md:h-8 w-auto" loading="lazy" />
                <img src="/payments/yappy.svg" alt="Yappy" className="h-7 md:h-8 w-auto" loading="lazy" />
              </div>
            </div>

            <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.5fr_1fr] gap-6 items-start">
              <QuoteForm />

              <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-primary/15 shadow-sm p-6 sm:p-8">
                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center mb-5">
                  <MessageCircle className="h-6 w-6 text-brown" />
                </div>
                <h3 className="font-playfair font-semibold text-xl text-brown-dark mb-2">
                  ¿Preferís WhatsApp?
                </h3>
                <p className="text-sm text-brown-light/90 leading-relaxed mb-6">
                  Escribile directo a Vivi y te responde personalmente. El atajo
                  sigue disponible para quien quiere resolverlo por chat.
                </p>
                <Button
                  size="lg"
                  onClick={handleCotizarWhatsApp}
                  className="w-full bg-brown text-white hover:bg-brown-dark font-semibold px-8 py-4 rounded-full text-base shadow-lg transition-all duration-300 min-h-[52px]"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Cotizar por WhatsApp
                </Button>

                <div className="mt-6 border-t border-primary/15 pt-6">
                  <h4 className="font-playfair font-semibold text-lg text-brown-dark mb-3">
                    ¿Preferís que te llamemos?
                  </h4>
                  <CallbackForm />
                </div>
              </div>
            </div>

            <div className="max-w-5xl mx-auto mt-10">
              <LeadMagnet />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Index;
